import React, { useMemo } from 'react';
import { FileError } from 'react-dropzone';
import { AppButton, Box, InvalidDocumentIcon, PasswordProtectedDocumentIcon, Text } from '@common-fe/common-fe';

import { MAX_SIZE_OF_FILE_IN_MEGABYTES } from '@/common/constants';
import { FileErrorCode } from '@/components/elements/DropScan/dropScan.types';
import { ModalWrapper } from '@/components/wrappers';

interface Props {
  fileError?: FileError;
  onClose: () => void;
  onUploadAnother?: () => void;
}

export const UploadFileErrorPopupContent: React.FC<Props> = ({ fileError, onClose, onUploadAnother }) => {
  const errorIcon = useMemo(() => {
    switch (fileError?.code) {
    case FileErrorCode.PasswordProtectedDocument: 
      return <PasswordProtectedDocumentIcon size="48px" />;
    default:
      return <InvalidDocumentIcon size="48px" />;
    }
  }, [fileError?.code]);

  const errorTitle = useMemo(() => {
    switch (fileError?.code) {
    case FileErrorCode.FileInvalidType: 
      return 'Unsupported File Format';
    case FileErrorCode.InvalidFile:
      return 'Invalid File';
    case FileErrorCode.DocumentNameIsTooLong:
      return 'The file name is too long. To upload it, please rename the file to be 200 characters or fewer.';
    case FileErrorCode.PasswordProtectedDocument:
      return 'Uploaded document is password protected!';
    default: 
      return 'We can’t upload your document';
    }
  }, [fileError?.code]);

  const errorDescription = useMemo(() => {
    switch (fileError?.code) {
    case FileErrorCode.FileInvalidType: 
      return (
        <>
          <Text>Please upload file in such formats PDF, JPEG, JPG, PNG, TIF, HEIC, HEIF.</Text>
          <br />
          <Text>Max. file size: {MAX_SIZE_OF_FILE_IN_MEGABYTES} MB</Text>
        </>
      );
    case FileErrorCode.InvalidFile: 
      return (
        <>
          <Text>The uploaded file does not match the expected format.</Text>
          <br />
          <Text>Please ensure the file is a valid PDF, JPEG, JPG, PNG, TIF, HEIC, or HEIF.</Text>
          <br />
          <Text>Max. file size: {MAX_SIZE_OF_FILE_IN_MEGABYTES} MB</Text>
        </>
      );
    case FileErrorCode.DocumentNameIsTooLong:
      return null;
    case FileErrorCode.PasswordProtectedDocument:
      return 'We cannot upload this type of document. Please export or save it as a new unprotected version or take a picture of your document.';
    default: 
      return 'The document you uploaded doesn’t meet our requirements. Please convert the file into an approved file type such as: PDF, HEIF, JPEG, JPG, PNG, TIFF and file size up to 20MB.';
    }
  }, [fileError?.code]);

  return (
    <Box align="center" pad="spacing24">
      {errorIcon}
      <Text
        margin={{ vertical: 'spacing12' }}
        textAlign="center"
        size="2xl"
        weight={700}
      >
        {errorTitle}
      </Text>
      <Text
        margin={{ bottom: 'spacing24' }}
        textAlign="center"
        style={{ textWrap: 'balance' }}
      >
        {errorDescription}
      </Text>
      <Box direction="row" gap="spacing12">
        <AppButton
          onClick={onClose}
          buttonType="secondary"
          width="220px"
        >
          Close
        </AppButton>
        <AppButton
          onClick={onUploadAnother ?? onClose}
          width="220px"
        >
          Upload Another Document
        </AppButton>
      </Box>
    </Box>
  );
};

const UploadFileErrorPopup: React.FC<Props> = ({ fileError, onClose, onUploadAnother }) => (
  <ModalWrapper visible onSetVisible={onClose}>
    <UploadFileErrorPopupContent
      fileError={fileError}
      onClose={onClose}
      onUploadAnother={onUploadAnother}
    />
  </ModalWrapper>
);

export default UploadFileErrorPopup;
